import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { useGetEventCTAButtonsDataQuery } from '../CtaButtons/CtaButtons.graphql.generated';
import { isRsvpEffectivelyEnabled } from '@apps/guest/packages/layout-engine/common/utils/legacyRsvpQuestions';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';
import { useState } from 'react';
import type { BackButtonCallBackType } from '@apps/guest/packages/layout-engine/components/DialogStyled/DialogStyled';
import type { CtaButtonsSessionData } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons.types';
import type { FindInviteSuccessBehavior } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog.types';

interface UnlockGuestControllerTypes
  extends Readonly<{
    hasUnlocked: boolean;
    isRsvpPasswordProtected: boolean;
    shouldDisplayFindInvite: boolean;
    shouldSkipPasswordGate: boolean;
    findInviteSuccessBehavior: FindInviteSuccessBehavior;
    eventId: string;
    showBackButton: boolean;
    setShowBackButton: (p: boolean) => void;
    onBackButtonClick: (cb: BackButtonCallBackType) => void;
  }> {}

function useGuestUnlockInfoController() {
  const { eventName } = useEventNameMatch();
  const [showBackButton, setShowBackButton] = useState<boolean>(false);
  const disableLegacyRsvp = useProFeature(EventFeature.disableLegacyRsvp);

  const { data: sessionData } = useGetEventCTAButtonsDataQuery({
    variables: { name: eventName || '' },
    batchMode: 'fast',
    ssr: false,
  });

  const event: CtaButtonsSessionData = sessionData?.eventByName ?? {};
  const { activeSession, id: eventId = '', rsvpPage } = event;
  const { hasUnlocked = false, eventCTA } = activeSession || {};
  // Only a live, password-protected RSVP page is an invite-backed unlock worth routing to Find Invite.
  // Mirror the CTA's effective-RSVP check (and exclude a hidden/disabled RSVP) so password-only locks
  // (date/location, private pages) don't push guests without an invite into a dead-end lookup.
  const isRsvpEnabled = !disableLegacyRsvp && isRsvpEffectivelyEnabled(eventCTA?.rsvpIsEnabled, event.hasEnabledLegacyRsvpQuestions);
  const isRsvpPasswordProtected = isRsvpEnabled && (rsvpPage?.private ?? false) && !rsvpPage?.disabled;

  const onBackButtonClick = (cb: BackButtonCallBackType) => {
    document.getElementById('backButton')?.addEventListener('click', () => {
      cb();
    });
  };

  return {
    hasUnlocked,
    isRsvpPasswordProtected,
    eventId,
    showBackButton,
    setShowBackButton,
    onBackButtonClick,
  };
}

export { UnlockGuestControllerTypes, useGuestUnlockInfoController };
